import { Clock, PencilLine, RotateCcw, Send, Sun, Sunrise, Trash2 } from 'lucide-react';

import { Badge } from '@/components/ui/Badge';
import {
  EXAM_BALANCE_PLAN_LABEL,
  EXAM_SESSION_LABEL,
  EXAM_TYPE_LABEL,
  EXAM_TYPE_TONE,
} from '@/features/teacher/exams/constants';
import { formatDate } from '@/lib/format';
import { cn } from '@/lib/utils';
import type { Exam } from '@/types/exam';
import { subjectTheme } from '../subjectTheme';
import { formatClockTime } from '../timetableWeek';

/**
 * The scheduled papers, which can be edited, deleted and published — or the
 * deleted ones, whose only action is to restore them. One table for both, so a
 * deleted paper reads exactly as it did on the timetable.
 */
export type ExamTimetableTableProps = { exams: Exam[] } & (
  | {
      variant?: 'scheduled';
      onEdit: (exam: Exam) => void;
      onRemove: (exam: Exam) => void;
      /** Releases a draft as it stands. Offered only on a draft. */
      onPublish: (exam: Exam) => void;
      /** The draft whose Publish is in flight, so its button can say so. */
      publishingUuid: string | null;
    }
  | {
      variant: 'deleted';
      onRestore: (exam: Exam) => void;
    }
);

/**
 * The examination timetable for one section.
 *
 * Every column is read off the paper. The session badge and the timings used to
 * be decided by the row's position in the list — even rows were mornings, odd
 * rows afternoons, all of them "09:30 AM – 12:30 PM" — so the same paper
 * changed session the moment another was scheduled before it. They are stored
 * on the paper now, and a paper set before that has timings simply says so
 * rather than borrowing somebody else's.
 */
export function ExamTimetableTable(props: ExamTimetableTableProps) {
  const { exams } = props;
  const isDeletedList = props.variant === 'deleted';

  return (
    <div className="rounded-3xl bg-white dark:bg-[#111c2e] border border-slate-200 dark:border-slate-800 overflow-x-auto custom-scrollbar shadow-sm print:rounded-none print:border-slate-300 print:shadow-none print:overflow-visible">
      <table className="w-full border-collapse text-sm min-w-[900px] relative print:min-w-full print:w-full print:text-xs">
        <caption className="sr-only">
          {isDeletedList
            ? 'Deleted examination papers for this class section.'
            : 'Scheduled examination papers for this class section.'}
        </caption>
        <thead>
          <tr className="bg-slate-100 dark:bg-[#0b1322] border-b border-slate-200 dark:border-slate-800 text-[14px] font-bold tracking-wide text-slate-700 dark:text-slate-300 print:bg-slate-100 print:text-black print:border-slate-300">
            <th
              scope="col"
              className="sticky left-0 z-20 bg-slate-100 dark:bg-[#0b1322] px-4 py-3.5 text-left font-bold w-36 shadow-2xs print:static print:bg-slate-100 print:shadow-none print:text-black"
            >
              Exam date
            </th>
            <th scope="col" className="px-4 py-3.5 text-left font-bold w-60 print:text-black">
              Exam timings
            </th>
            <th scope="col" className="px-4 py-3.5 text-left font-bold w-40 print:text-black">
              Subject
            </th>
            <th scope="col" className="px-4 py-3.5 text-left font-bold print:text-black">
              Paper &amp; rest of the day
            </th>
            <th scope="col" className="px-4 py-3.5 text-left font-bold w-36 whitespace-nowrap print:text-black">
              Type
            </th>
            <th scope="col" className="px-4 py-3.5 text-left font-bold w-36 print:text-black">
              Marks
            </th>
            <th
              scope="col"
              className="sticky right-0 z-20 bg-slate-100 dark:bg-[#0b1322] px-4 py-3.5 text-center font-bold w-20 shadow-2xs print:hidden"
            >
              Action
            </th>
          </tr>
        </thead>

        <tbody className="divide-y divide-slate-100 dark:divide-slate-800/70 print:divide-slate-300">
          {exams.map((exam) => {
            const date = new Date(`${exam.examDate}T00:00:00`);
            const theme = subjectTheme(exam.subject.name);
            const isMorning = exam.session === 'MORNING';
            const startAt = formatClockTime(exam.startTime);
            const endAt = formatClockTime(exam.endTime);

            return (
              <tr
                key={exam.uuid}
                className="group hover:bg-slate-50/70 dark:hover:bg-[#142033]/40 transition-colors print:border-slate-300"
              >
                <td className="sticky left-0 z-10 bg-white dark:bg-[#111c2e] group-hover:bg-slate-50 dark:group-hover:bg-[#142033] px-4 py-2.5 align-middle shadow-[2px_0_5px_-2px_rgba(0,0,0,0.06)] transition-colors print:static print:bg-white print:shadow-none">
                  <div className="font-black text-slate-900 dark:text-white text-xs sm:text-sm print:text-black">
                    {date.toLocaleDateString('en-IN', {
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric',
                    })}
                  </div>
                  <div className="text-[11px] text-indigo-600 dark:text-indigo-400 font-bold print:text-slate-700">
                    {date.toLocaleDateString('en-IN', { weekday: 'long' })}
                  </div>
                </td>

                <td className="px-4 py-2.5 align-middle">
                  {exam.session === null ? (
                    <span className="text-[11px] font-bold text-slate-400 dark:text-slate-500">
                      No timings recorded — edit the paper to add them
                    </span>
                  ) : (
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span
                        className={cn(
                          'inline-flex items-center gap-1 text-[11px] font-black border px-2 py-0.5 rounded-md shrink-0',
                          isMorning
                            ? 'bg-amber-50 dark:bg-amber-950/70 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-800'
                            : 'bg-blue-50 dark:bg-blue-950/70 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-800',
                        )}
                      >
                        {isMorning ? (
                          <Sunrise className="h-3 w-3 text-amber-600" />
                        ) : (
                          <Sun className="h-3 w-3 text-blue-600" />
                        )}
                        <span>{EXAM_SESSION_LABEL[exam.session]}</span>
                      </span>
                      <span className="inline-flex items-center gap-1 text-[11px] text-slate-700 dark:text-slate-300 font-bold bg-slate-100 dark:bg-slate-800 px-2 py-0.5 rounded-md shrink-0 print:bg-slate-100 print:text-black">
                        <Clock className="h-3 w-3 text-slate-400" />
                        <span>
                          {startAt} – {endAt}
                        </span>
                      </span>
                    </div>
                  )}
                </td>

                <td className="px-4 py-2.5 align-middle">
                  <span
                    className={cn(
                      'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-bold border print:bg-slate-50 print:text-black print:border-slate-300',
                      theme.cardBg,
                    )}
                  >
                    {theme.icon}
                    <span className="capitalize">{exam.subject.name}</span>
                  </span>
                </td>

                <td className="px-4 py-2.5 align-middle">
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="font-extrabold text-slate-900 dark:text-white text-xs sm:text-sm print:text-black">
                      {exam.name}
                    </span>
                    {isDeletedList && exam.suspendedAt && (
                      <Badge tone="critical" className="text-[11px]">
                        Deleted {formatDate(exam.suspendedAt)}
                      </Badge>
                    )}
                    {!isDeletedList && exam.publication === 'DRAFT' && (
                      <Badge tone="warning" className="text-[11px] print:hidden">
                        Draft — not shown to the class
                      </Badge>
                    )}
                    {exam.balancePlan && (
                      <span className="inline-flex items-center gap-1 text-[11px] font-bold text-slate-600 dark:text-slate-400 bg-slate-50 dark:bg-slate-800/80 px-2.5 py-0.5 rounded-md border border-slate-200/80 dark:border-slate-700 print:bg-slate-50 print:text-slate-700">
                        {EXAM_BALANCE_PLAN_LABEL[exam.balancePlan]}
                      </span>
                    )}
                  </div>
                </td>

                <td className="px-4 py-2.5 align-middle whitespace-nowrap">
                  <Badge
                    tone={EXAM_TYPE_TONE[exam.examType]}
                    className="font-bold text-[11px] px-2.5 py-1 whitespace-nowrap inline-block"
                  >
                    {EXAM_TYPE_LABEL[exam.examType]}
                  </Badge>
                </td>

                <td className="px-4 py-2.5 align-middle">
                  <div className="flex items-center gap-2 font-mono text-xs font-bold flex-wrap print:text-black">
                    <span className="text-slate-700 dark:text-slate-300 print:text-black">Max: {exam.maxMarks}</span>
                    <span className="text-emerald-600 dark:text-emerald-400 print:text-black">
                      Pass: {exam.passMarks}
                    </span>
                  </div>
                </td>

                <td className="sticky right-0 z-2 bg-white dark:bg-[#111c2e] group-hover:bg-slate-50 dark:group-hover:bg-[#142033] px-4 py-2.5 align-middle text-center shadow-[-2px_0_5px_-2px_rgba(0,0,0,0.06)] transition-colors print:hidden">
                  {props.variant === 'deleted' ? (
                    <button
                      type="button"
                      title={`Restore ${exam.name}`}
                      aria-label={`Restore ${exam.name}`}
                      onClick={() => props.onRestore(exam)}
                      className="p-1 text-slate-400 hover:text-emerald-600 dark:hover:text-emerald-400 transition-colors cursor-pointer rounded-lg hover:bg-emerald-50 dark:hover:bg-emerald-950/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500"
                    >
                      <RotateCcw className="h-4 w-4" />
                    </button>
                  ) : (
                    <div className="flex items-center justify-center gap-1">
                      {exam.publication === 'DRAFT' && (
                        <button
                          type="button"
                          title={`Publish ${exam.name}`}
                          aria-label={`Publish ${exam.name}`}
                          onClick={() => props.onPublish(exam)}
                          disabled={props.publishingUuid !== null}
                          className="p-1 text-slate-400 hover:text-emerald-600 dark:hover:text-emerald-400 transition-colors cursor-pointer rounded-lg hover:bg-emerald-50 dark:hover:bg-emerald-950/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          <Send className={cn('h-4 w-4', props.publishingUuid === exam.uuid && 'animate-pulse')} />
                        </button>
                      )}
                      <button
                        type="button"
                        title={`Edit ${exam.name}`}
                        aria-label={`Edit ${exam.name}`}
                        onClick={() => props.onEdit(exam)}
                        className="p-1 text-slate-400 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors cursor-pointer rounded-lg hover:bg-indigo-50 dark:hover:bg-indigo-950/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
                      >
                        <PencilLine className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        title={`Remove ${exam.name}`}
                        aria-label={`Remove ${exam.name}`}
                        onClick={() => props.onRemove(exam)}
                        className="p-1 text-slate-400 hover:text-rose-600 transition-colors cursor-pointer rounded-lg hover:bg-rose-50 dark:hover:bg-rose-950/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-rose-500"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
